import React from 'react'
import { Form } from 'react-bootstrap'
import { isMobile } from 'react-device-detect'
import { Link } from 'react-router-dom'
import {
  $DCprimaryActiveColor,
  DcButton,
  DcCheckbox,
  DcDiv,
  DcFlexBox,
  DcInput,
  DcLeftPanel,
  DcRightPanel,
  DcText,
  PixelBusinessDiagram,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelModal,
  PixelText,
  colors,
} from 'pixel-base'
import styled from 'styled-components'

import { resetPassword, ResetPasswordResponse } from 'utils/api-routes-auth'
import { AuthContext } from 'contexts/AuthContext'
import { IPixelFactory } from '../../../../decl.d'

interface LoginData {
  email: string
  password: string
}

export const Login = () => {
  const { dispatchLogin, formError } = React.useContext(AuthContext)
  const [internalError, setError] = React.useState({
    form: 'login',
    enable: false,
    field: '',
    error: '',
  })
  const [showResetModal, setShowResetModal] = React.useState(false)
  const [resetEmailMessage, setResetEmailMessage] = React.useState('')
  React.useEffect(() => {
    if (formError.form === 'login') setError(formError)
  }, [formError])

  const getError = (field: string) => {
    return internalError.enable && internalError.field === field ? (
      <PixelText customColor={colors.red} textSize="80%">
        {internalError.error}
      </PixelText>
    ) : null
  }

  const [data, setData] = React.useState<LoginData>({
    email: '',
    password: '',
  })
  const [rememberMe, setRememberMe] = React.useState(true)

  const pixelFactory = React.useContext<IPixelFactory>(PixelFactoryContext)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    dispatchLogin(data)
  }

  const dispatchReset = (data: LoginData) => {
    pixelFactory.showPageLoader()
    resetPassword(data.email).subscribe((res: ResetPasswordResponse) => {
      setResetEmailMessage(res.message)
      pixelFactory.hidePageLoader()
    })
  }

  const handleSubmitResetForm = (e: React.FormEvent) => {
    e.preventDefault()
    dispatchReset(data)
  }

  return (
    <>
      <DcFlexBox
        width="100%"
        height="100vh"
        justifyContent="center"
        alignItems="center"
        backgroundColor={colors.grayLighter}
      >
        <DcFlexBox
          border={`1px solid ${colors.grayBorderLight}`}
          boxShadow={`0 1px 0 ${colors.grayBorderLight}50`}
          width={isMobile ? '100%' : '1272px'}
          height={isMobile ? '100%' : '684px'}
          borderRadius="15px"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'center' : 'flex-start'}
        >
          {!isMobile && (
            <DcLeftPanel
              backgroundColor={colors.authBg}
              borderRadius={isMobile ? '0px' : '15px 0 0 15px'}
              width={isMobile ? '100%' : '59%'}
              style={{
                overflow: 'hidden',
              }}
            >
              <PixelBusinessDiagram />
            </DcLeftPanel>
          )}
          <DcRightPanel
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            padding="40px"
            width={isMobile ? '100%' : '42%'}
            borderRadius={isMobile ? '0px' : '0 15px 15px 0'}
            backgroundColor={colors.white}
          >
            <DcFlexBox
              width="auto"
              height="auto"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              margin="0 0 2rem 0"
            >
              <DcText
                customColor={colors.grayTextDark}
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: '700',
                  fontSize: '1.25rem',
                  marginBottom: '6px',
                }}
              >
                Login to Account
              </DcText>
              <PixelText
                customColor={colors.grayMedium}
                style={{
                  fontSize: '.875rem',
                  textAlign: 'center',
                }}
              >
                Please sign-in to your account and start the adventure.
              </PixelText>
            </DcFlexBox>

            <Form
              style={{
                marginTop: '-16px',
                width: '100%',
                textAlign: 'center',
              }}
              onSubmit={handleSubmit}
            >
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',
                    color: colors.grayTextDark,
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  Email
                </DcText>
                <DcInput
                  placeholder="Enter Email"
                  style={{ padding: '9px 18px' }}
                  type="email"
                  value={data.email}
                  required
                  onChange={(e) =>
                    setData({
                      ...data,
                      email: e.currentTarget.value,
                    })
                  }
                  name="email"
                />
                {getError('email')}
              </DcDiv>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: '16px',
                  textAlign: 'left',
                }}
              >
                <DcText
                  fontSize=".875rem"
                  fontWeight="500"
                  customColor={colors.grayTextDark}
                  style={{
                    textAlign: 'left',
                    margin: '0 0 0.5rem 0',
                  }}
                >
                  Password
                </DcText>

                <DcInput
                  type="password"
                  name="password"
                  style={{
                    padding: '9px 18px',
                  }}
                  required
                  value={data.password}
                  onChange={(e) =>
                    setData({
                      ...data,
                      password: e.currentTarget.value,
                    })
                  }
                  placeholder="Enter Password"
                />
                {getError('password')}
              </DcDiv>
              <DcFlexBox
                style={{ marginTop: '16px' }}
                height="auto"
                justifyContent="space-between"
              >
                <DcCheckbox
                  type="checkbox"
                  value={rememberMe.toString()}
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  label={'Remember me'}
                  name="rememberMe"
                  disabled={false}
                />
                <DcButton
                  variant="link"
                  padding="0px"
                  style={{
                    cursor: 'pointer',
                    color: $DCprimaryActiveColor,
                    padding: '0px !important',
                    fontSize: '.875rem',
                  }}
                  onClick={() => setShowResetModal(true)}
                >
                  Forgot password?
                </DcButton>
              </DcFlexBox>
              <PixelDiv style={{ marginTop: '16px' }}>
                <DcButton
                  style={{
                    width: '100%',
                    padding: '9px 18px',
                  }}
                  type="submit"
                  variant="gradient"
                  width="100%"
                >
                  Login to account
                </DcButton>
              </PixelDiv>
              <DcDiv style={{ display: 'flex', justifyContent: 'center' }}>
                <Divider>OR</Divider>
              </DcDiv>
              <PixelDiv width="100%" height="auto">
                {internalError.enable && internalError.field === '' && (
                  <PixelText customColor={colors.red} textSize="80%">
                    {internalError.error}
                  </PixelText>
                )}
              </PixelDiv>
              <PixelText
                textSize="14px"
                style={{
                  fontWeight: '400',
                  letterSpacing: 'normal',
                  lineHeight: '19.6px',
                  textAlign: 'center',
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '3px',
                }}
                customColor={colors.grayTextDark}
              >
                {"Don't have an account?"}
                <Link to={'/auth/signup'} style={{ textDecoration: 'none' }}>
                  <DcButton
                    variant="link"
                    padding="0"
                    style={{
                      fontSize: '.875rem',
                      color: `${colors.primary} !important`,
                    }}
                  >
                    Register.
                  </DcButton>
                </Link>
              </PixelText>
            </Form>
          </DcRightPanel>
        </DcFlexBox>
      </DcFlexBox>

      <PixelModal
        show={showResetModal}
        handleClose={() => setShowResetModal(false)}
        title="Forget Password?"
        footer={
          !resetEmailMessage ? (
            <PixelFlexBox
              justifyContent="flex-end"
              alignItems="center"
              gap="10px"
            >
              <DcButton
                variant="secondary"
                onClick={() => setShowResetModal(false)}
              >
                Close
              </DcButton>
              <DcButton
                variant="primary"
                type="submit"
                onClick={() => dispatchReset(data)}
              >
                Reset Password
              </DcButton>
            </PixelFlexBox>
          ) : (
            <DcButton
              variant="primary"
              onClick={() => setShowResetModal(false)}
            >
              Okay
            </DcButton>
          )
        }
      >
        {!resetEmailMessage ? (
          <Form onSubmit={handleSubmitResetForm}>
            <PixelFlexBox flexDirection="column" gap="20px">
              <PixelText
                customColor={colors.grayMedium}
                style={{ lineHeight: '16px', fontWeight: '400' }}
                textSize="16px"
              >
                {'No worries, we will send you reset instructions.'}
              </PixelText>
              <DcInput
                value={data.email}
                required
                onChange={(e) =>
                  setData({
                    ...data,
                    email: e.currentTarget.value,
                  })
                }
                type="email"
                name="email"
                placeholder="Email"
              />
            </PixelFlexBox>
          </Form>
        ) : (
          <>
            <PixelFlexBox>
              <DcText className="h4">{resetEmailMessage}</DcText>
            </PixelFlexBox>
          </>
        )}
      </PixelModal>
    </>
  )
}

const BgImage = styled.div`
  background-image: url('https://react.nioboard.themenio.com/static/media/a.23bd4f13bb2b41d70a8f.png');
  height: 150px;
  width: 100%;
  background-size: cover;
  padding: 150px 20px 20px 20px;

  @media (min-width: 768px) {
    height: 200px;
    width: 504px;
    padding: 200px 40px 40px 40px;
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80%;
  margin: 20px 0;
  font-size: 14px;
  color: ${colors.grayText};
  position: relative;
  text-align: center;

  &::before,
  &::after {
    content: '';
    flex-grow: 1;
    height: 2px;
    background-color: ${colors.grayBorderLight};
    position: relative;
  }

  &::before {
    margin-right: 10px;
  }

  &::after {
    margin-left: 10px;
  }

  @media (min-width: 768px) {
    width: 30%;
  }
`
